import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

// ── Helpers ───────────────────────────────────────────────────────────────────
function calcularRacha(actividad) {
  if (!actividad.length) return 0;

  // Obtener días únicos con actividad (sin hora)
  const diasUnicos = [...new Set(
    actividad
      .map(a => a.fecha?.toDate?.() || new Date(0))
      .filter(d => d.getTime() > 0)
      .map(d => {
        const dia = new Date(d);
        dia.setHours(0, 0, 0, 0);
        return dia.getTime();
      })
  )].sort((a, b) => b - a); // Ordenar de más reciente a más antiguo

  if (diasUnicos.length === 0) return 0;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const hoyMs = hoy.getTime();
  const ayerMs = hoyMs - 86400000;

  // La racha actual solo cuenta si hay actividad hoy o ayer
  if (diasUnicos[0] !== hoyMs && diasUnicos[0] !== ayerMs) return 0;

  // Contar días consecutivos desde el más reciente
  let racha = 1;
  for (let i = 1; i < diasUnicos.length; i++) {
    const diff = diasUnicos[i - 1] - diasUnicos[i];
    if (diff === 86400000) {
      racha++;
    } else {
      break; // Se rompe la racha
    }
  }
  return racha;
}

// ── Barra de progreso visual ──────────────────────────────────────────────────
function BarraGenero({ genero, cantidad, maximo, color = '#1DB954' }) {
  const pct = maximo > 0 ? (cantidad / maximo) * 100 : 0;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', color: '#aaa', fontWeight: '500' }}>{genero}</span>
        <span style={{ fontSize: '12px', fontWeight: 'bold', color, background: `${color}18`, padding: '2px 8px', borderRadius: '20px' }}>{cantidad} {cantidad === 1 ? 'libro' : 'libros'}</span>
      </div>
      <div style={{ height: '4px', background: '#1a1a1a', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${color}99, ${color})`, borderRadius: '99px', transition: '1s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: `0 0 8px ${color}44` }} />
      </div>
    </div>
  );
}

// ── Tarjeta de stat ───────────────────────────────────────────────────────────
function StatCard({ valor, label, sublabel, color = '#1DB954' }) {
  return (
    <div style={{ background: '#111', borderRadius: '16px', padding: '18px 14px', border: '1px solid #1e1e1e', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      {/* Glow de fondo */}
      <div style={{ position: 'absolute', top: '-20px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '60px', background: color, borderRadius: '50%', filter: 'blur(30px)', opacity: 0.12 }} />
      <p style={{ margin: '0 0 4px', fontSize: '30px', fontWeight: 900, color, letterSpacing: '-1px', lineHeight: 1 }}>{valor}</p>
      <p style={{ margin: '0 0 3px', fontSize: '12px', color: 'white', fontWeight: 'bold', letterSpacing: '0.2px' }}>{label}</p>
      {sublabel && <p style={{ margin: 0, fontSize: '10px', color: '#333', lineHeight: '1.3' }}>{sublabel}</p>}
    </div>
  );
}

// ── Componente principal de estadísticas ──────────────────────────────────────
export function EstadisticasAvanzadas({ userId, esPremium, onUpgrade }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const cargar = async () => {
      setCargando(true);
      try {
        const anioActual = new Date().getFullYear();
        const anioAnterior = anioActual - 1;

        // Estados de lectura
        const estadosSnap = await getDocs(
          query(collection(db, "estados_lectura"), where("userId", "==", userId))
        );
        const estados = estadosSnap.docs.map(d => d.data());

        // Libros leídos este año y año anterior
        const leidosEsteAnio = estados.filter(e => {
          if (e.estado !== 'leido') return false;
          const fecha = e.fecha?.toDate?.();
          return fecha && fecha.getFullYear() === anioActual;
        }).length;

        const leidosAnioAnterior = estados.filter(e => {
          if (e.estado !== 'leido') return false;
          const fecha = e.fecha?.toDate?.();
          return fecha && fecha.getFullYear() === anioAnterior;
        }).length;

        const totalLeidos = estados.filter(e => e.estado === 'leido').length;
        const totalLeyendo = estados.filter(e => e.estado === 'leyendo').length;
        const totalPendientes = estados.filter(e => e.estado === 'quiero_leer').length;

        // Páginas estimadas (media de 300 páginas por libro)
        const paginasEstimadas = totalLeidos * 300;

        // Géneros más leídos
        const generosCount = {};
        estados.filter(e => e.estado === 'leido' && e.libro?.genero).forEach(e => {
          const g = e.libro.genero;
          generosCount[g] = (generosCount[g] || 0) + 1;
        });

        // También contar por título si no hay género
        const generosOrdenados = Object.entries(generosCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        // Actividad reciente para racha
        const actSnap = await getDocs(
          query(collection(db, "actividad_reciente"), where("userId", "==", userId), orderBy("fecha", "desc"), limit(365))
        );
        const actividad = actSnap.docs.map(d => d.data());
        const racha = calcularRacha(actividad);

        // Valoraciones
        const valSnap = await getDocs(
          query(collection(db, "valoraciones"), where("userId", "==", userId))
        );
        const valoraciones = valSnap.docs.map(d => d.data());
        const mediaVotos = valoraciones.length > 0
          ? Math.round(valoraciones.reduce((a, b) => a + b.estrellas, 0) / valoraciones.length * 10) / 10
          : 0;

        // Mensajes enviados
        const userStats = await getDocs(
          query(collection(db, "user_stats"), where("__name__", "==", userId))
        );
        const mensajes = userStats.docs[0]?.data()?.mensajes_enviados || 0;

        // Ritmo de lectura — libros leídos por mes este año
        const mesesTranscurridos = new Date().getMonth() + 1;
        const ritmoMensual = Math.round(leidosEsteAnio / mesesTranscurridos * 10) / 10;

        // Récord anual — el año con más libros leídos
        const porAnio = {};
        estados.filter(e => e.estado === 'leido' && e.fecha?.toDate).forEach(e => {
          const anio = e.fecha.toDate().getFullYear();
          porAnio[anio] = (porAnio[anio] || 0) + 1;
        });
        const recordAnual = Math.max(0, ...Object.values(porAnio));
        const anioRecord = Object.entries(porAnio).find(([, v]) => v === recordAnual)?.[0] || '';

        setDatos({
          leidosEsteAnio,
          leidosAnioAnterior,
          totalLeidos,
          totalLeyendo,
          totalPendientes,
          paginasEstimadas,
          generosOrdenados,
          racha,
          mediaVotos,
          mensajes,
          totalValoraciones: valoraciones.length,
          ritmoMensual,
          recordAnual,
          anioRecord,
        });
      } catch (e) { console.error("Error cargando estadísticas:", e); }
      setCargando(false);
    };
    cargar();
  }, [userId]);

  // Vista bloqueada para usuarios no premium
  if (!esPremium) {
    return (
      <div style={{ position: 'relative', marginBottom: '32px' }}>
        {/* Preview borrosa */}
        <div style={{ filter: 'blur(6px)', pointerEvents: 'none', opacity: 0.4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
            {[['12', 'Libros leídos', '2026'], ['3.600', 'Páginas leídas', 'estimadas'], ['7', 'Días de racha', 'consecutivos']].map(([v, l, s], i) => (
              <StatCard key={i} valor={v} label={l} sublabel={s} />
            ))}
          </div>
          <div style={{ background: '#141414', borderRadius: '12px', padding: '16px', border: '1px solid #1e1e1e' }}>
            {['Ficción', 'Thriller', 'Historia'].map((g, i) => (
              <BarraGenero key={i} genero={g} cantidad={5 - i} maximo={5} />
            ))}
          </div>
        </div>
        {/* Overlay Premium */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', borderRadius: '12px', backdropFilter: 'blur(2px)' }}>
          <p style={{ fontSize: '24px', marginBottom: '8px' }}>✦</p>
          <p style={{ fontWeight: 900, fontSize: '16px', color: 'white', margin: '0 0 6px' }}>Estadísticas Premium</p>
          <p style={{ color: '#555', fontSize: '13px', margin: '0 0 16px', textAlign: 'center' }}>Descubre tus hábitos de lectura en detalle</p>
          <button onClick={onUpgrade}
            style={{ background: 'linear-gradient(135deg, #6a3fa0, #a78bfa)', color: 'white', border: 'none', borderRadius: '20px', padding: '10px 24px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>
            Desbloquear Premium
          </button>
        </div>
      </div>
    );
  }

  if (cargando) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '32px' }}>
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{ height: '90px', background: '#141414', borderRadius: '12px', animation: 'pulse 1.5s infinite' }} />
        ))}
      </div>
    );
  }

  if (!datos) return null;

  const maxGenero = datos.generosOrdenados[0]?.[1] || 1;
  const coloresGeneros = ['#1DB954', '#a78bfa', '#38bdf8', '#ff9800', '#f44336'];

  const diferenciaAnual = datos.leidosEsteAnio - datos.leidosAnioAnterior;

  return (
    <div style={{ marginBottom: '32px' }}>
      {/* Stats principales — fila de 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
        <StatCard
          valor={datos.leidosEsteAnio}
          label="Este año"
          sublabel={diferenciaAnual > 0 ? `+${diferenciaAnual} vs ${new Date().getFullYear() - 1}` : diferenciaAnual < 0 ? `${diferenciaAnual} vs ${new Date().getFullYear() - 1}` : `= que ${new Date().getFullYear() - 1}`}
          color="#1DB954"
        />
        <StatCard
          valor={datos.totalLeidos}
          label="Total leídos"
          sublabel={`~${(datos.paginasEstimadas / 1000).toFixed(1)}k páginas`}
          color="#a78bfa"
        />
        <StatCard
          valor={`${datos.racha}d`}
          label="Racha"
          sublabel="días consecutivos"
          color="#f59e0b"
        />
      </div>

      {/* Fila secundaria */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
        <StatCard valor={datos.totalLeyendo} label="Leyendo" sublabel="ahora mismo" color="#38bdf8" />
        <StatCard valor={datos.totalPendientes} label="Pendientes" sublabel="por leer" color="#555" />
        <StatCard valor={datos.mediaVotos > 0 ? `${datos.mediaVotos}★` : '—'} label="Tu media" sublabel={`${datos.totalValoraciones} valoradas`} color="#FFD700" />
      </div>

      {/* Ritmo y récord */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        <StatCard
          valor={`${datos.ritmoMensual}`}
          label="Ritmo lector"
          sublabel="libros/mes este año"
          color="#1DB954"
        />
        {datos.recordAnual > 0 && (
          <StatCard
            valor={datos.recordAnual}
            label="Récord personal"
            sublabel={`libros en ${datos.anioRecord}`}
            color="#f59e0b"
          />
        )}
      </div>

      {/* Géneros más leídos */}
      {datos.generosOrdenados.length > 0 && (
        <div style={{ background: '#111', borderRadius: '16px', padding: '18px', border: '1px solid #1e1e1e', marginBottom: '8px' }}>
          <p style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 16px', fontWeight: 'bold' }}>Géneros favoritos</p>
          {datos.generosOrdenados.map(([genero, cant], i) => (
            <BarraGenero key={genero} genero={genero} cantidad={cant} maximo={maxGenero} color={coloresGeneros[i]} />
          ))}
        </div>
      )}

      {/* Mensajes enviados */}
      {datos.mensajes > 0 && (
        <div style={{ background: '#111', borderRadius: '16px', padding: '14px 18px', border: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', color: '#555' }}>Mensajes en salas</span>
          <span style={{ fontSize: '22px', fontWeight: 900, color: '#1DB954' }}>{datos.mensajes}</span>
        </div>
      )}
    </div>
  );
}
